import { log, track } from "@connect/observability";
import { readUpgradedClientToken } from "../../../../lib/buy/challenge-token-store";
import { buildGuestRecoveryChallengeUrl } from "../../../../lib/buy/challenge-urls";
import { requiresGuestConnect } from "../../../../lib/buy/is-guest-checkout";
import { runCreate } from "../../lib/run-create";
import type { PreCheckResult } from "../../../../lib/buy/pre-check.types";
import { checkBeforePayment, reportPreCheck, runPreCheck } from "../../lib/run-pre-check";
import { trackChallenge, trackTerminal } from "../../lib/track-outcome";
import type { RunWalletPaymentParams, WalletPhase } from "./wallet.types";

const PRE_CHECK_TIMEOUT_MS = 10_000;

// The sheet spins until the check settles, so it must not hang forever.
function withTimeout(check: Promise<PreCheckResult>): Promise<PreCheckResult> {
  return Promise.race([
    check,
    new Promise<PreCheckResult>((resolve) =>
      setTimeout(
        () => resolve({ outcome: "error", message: "The pre-check timed out" }),
        PRE_CHECK_TIMEOUT_MS,
      ),
    ),
  ]);
}

function enterPhase(
  phase: WalletPhase,
  busy: RunWalletPaymentParams["busy"],
  setPhase: RunWalletPaymentParams["setPhase"],
) {
  busy.current = phase !== "idle";
  setPhase(phase);
}

export async function runWalletPayment({
  wallet,
  quote,
  config,
  pm,
  present,
  isCancelled,
  setPhase,
  busy,
  kycStepUp,
  checkInSheet = false,
  preCheck = runPreCheck,
  checkFirst = checkBeforePayment,
  reportCheck = reportPreCheck,
  create = runCreate,
  readUpgraded = readUpgradedClientToken,
}: RunWalletPaymentParams): Promise<void> {
  // Set synchronously so a double tap can't create two transactions.
  if (busy.current) {
    return;
  }
  busy.current = true;
  // After a guest recovery the challenge frame stored the signed-in session under the mounted
  // token. Read per tap, so a dismissed sheet keeps it, and keep the mounted token as the key.
  const upgraded = readUpgraded(config.clientToken);
  const apiConfig = upgraded ? { ...config, clientToken: upgraded } : config;
  const handbackKey = config.clientToken;
  const analytics = { paymentMethod: wallet.tokenProvider };

  pm.send("buttonPressed");
  track.info("connect.buy_button.button_pressed", analytics);

  // A guest whose email and phone map to different customers must run Connect first.
  if (requiresGuestConnect(apiConfig.clientToken)) {
    trackChallenge("guest_mismatch", wallet.tokenProvider);
    pm.send("challenge", { kind: "frame", url: buildGuestRecoveryChallengeUrl(config, quote) });
    enterPhase("idle", busy, setPhase);
    return;
  }

  // Apple Pay only opens the sheet straight from the tap. Awaiting the pre-check first lost
  // the tap in some app WebViews ("show() must be triggered by user activation").
  enterPhase("sheet", busy, setPhase);
  const inSheet: { check: Promise<PreCheckResult> | null; result: PreCheckResult | null } = {
    check: null,
    result: null,
  };
  if (checkInSheet) {
    inSheet.check = withTimeout(checkFirst({ quote, config: apiConfig, handbackKey })).then(
      (check) => {
        inSheet.result = check;
        return check;
      },
    );
  }
  const result = await present(
    inSheet.check?.then((check) => {
      if (check.outcome !== "pass") {
        throw new Error("Pre-check did not pass");
      }
    }),
  );
  if (isCancelled()) {
    // Unmounted with the sheet open: close it without paying.
    if (result.status === "authorized") {
      await result.payment.complete("fail");
    }
    return;
  }
  // The pre-check closed the sheet before the customer could pay. Tell the partner only now,
  // so a challenge doesn't open over the closing sheet.
  if (result.status !== "authorized" && inSheet.result && inSheet.result.outcome !== "pass") {
    await reportCheck(inSheet.result, pm, wallet.tokenProvider, handbackKey);
    enterPhase("idle", busy, setPhase);
    return;
  }
  if (result.status === "dismissed") {
    track.info("connect.buy_button.sheet_dismissed", analytics);
    pm.send("cancelled", { code: "payment_sheet_dismissed", message: wallet.dismissedMessage });
    enterPhase("idle", busy, setPhase);
    return;
  }
  if (result.status === "failed") {
    log.warn(`${wallet.name} sheet failed`, { err: String(result.error) });
    const error = { code: "generic" as const, message: `${wallet.name} could not start` };
    trackTerminal({ kind: "error", payload: error }, wallet.tokenProvider);
    pm.send("error", error);
    enterPhase("idle", busy, setPhase);
    return;
  }

  const { payment } = result;
  track.info("connect.buy_button.authorized", analytics);
  // The token isn't used until create, so closing the sheet as failed here charges nothing.
  enterPhase("checking", busy, setPhase);
  const check = inSheet.check
    ? await inSheet.check
    : await preCheck({
        quote,
        config: apiConfig,
        handbackKey,
        pm,
        paymentMethod: wallet.tokenProvider,
      });
  if (isCancelled()) {
    await payment.complete("fail");
    return;
  }
  // After a challenge the customer taps again, so free the button.
  if (check.outcome !== "pass") {
    await payment.complete("fail");
    if (inSheet.check) {
      await reportCheck(check, pm, wallet.tokenProvider, handbackKey);
    }
    enterPhase("idle", busy, setPhase);
    return;
  }

  enterPhase("creating", busy, setPhase);
  const outcome = await create({
    quote,
    config: apiConfig,
    handbackKey,
    sessionUpgraded: Boolean(upgraded),
    pm,
    instrument: {
      kind: "wallet",
      tokenProvider: wallet.tokenProvider,
      token: payment.token,
      scheme: payment.scheme,
      billingAddress: payment.billingAddress,
      challengeToken: check.challengeToken,
    },
    isCancelled,
    kycStepUp,
  });
  // Apple's sheet spins until complete(), so close it only once we know the outcome.
  // A create-time challenge charged nothing, so the sheet must not show success: customers
  // took that as a payment and then thought they paid twice after the challenge.
  await payment.complete(outcome === "success" ? "success" : "fail");
  // After a challenge the customer taps again, so free the button.
  enterPhase(outcome === "challenge" ? "idle" : "done", busy, setPhase);
}
